import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Modal1 from '../../Modal1/Modal1';
import Button from '../../Button/Button';
import AssortmentMatchHint from './AssortmentMatchHint/AssortmentMatchHint';
import { getRequest, postRequest } from '../../../Helper/ApiClient';
import {
  IAssortmentMatch,
  IAssortmentMatchResult,
  IListingRequestPayload,
  LISTING_REQUESTS_MINE_QUERY_KEY,
  ListingRequestSource,
  ListingRequestType,
} from '../../../../Interface/ListingRequest';

const MATCH_MIN_LENGTH = 3;
const MATCH_DEBOUNCE_MS = 350;

type ListingRequestModalProps = {
  show: boolean;
  source: ListingRequestSource;
  onHide: () => void;
};

const ListingRequestModal = ({
  show,
  source,
  onHide,
}: ListingRequestModalProps) => {
  const [type, setType] = useState<ListingRequestType>('product');
  const [brandName, setBrandName] = useState<string>('');
  const [productName, setProductName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');
  const [consent, setConsent] = useState<boolean>(false);
  // The moment the customer confirmed that the suggestions miss what they want;
  // null while nothing is confirmed (see MatchConfirmation).
  const [matchConfirmedAt, setMatchConfirmedAt] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [sessionLost, setSessionLost] = useState<boolean>(false);
  const [debouncedMatch, setDebouncedMatch] = useState<{
    term: string;
    brand: string;
  }>({ term: '', brand: '' });

  const queryClient = useQueryClient();
  const mutation = postRequest('/api/onlineshop/listing-requests');
  const isProduct = 'product' === type;
  const matchTerm = isProduct ? productName : brandName;

  // The brand is part of the exact-match check for product requests - product
  // names repeat across brands, so the name alone must not block a request.
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setDebouncedMatch({
          term: matchTerm.trim(),
          brand: isProduct ? brandName.trim() : '',
        }),
      MATCH_DEBOUNCE_MS
    );

    return () => clearTimeout(timer);
  }, [matchTerm, brandName, isProduct]);

  const matchRequest = getRequest<IAssortmentMatchResult>(
    `/api/onlineshop/listing-requests/match?type=${type}&q=${encodeURIComponent(
      debouncedMatch.term
    )}&brand=${encodeURIComponent(debouncedMatch.brand)}`,
    ['listing-request-match', type, debouncedMatch.term, debouncedMatch.brand],
    { enabled: show && debouncedMatch.term.length >= MATCH_MIN_LENGTH }
  );

  // Every opening starts a new request, so nothing from the previous one stays behind.
  useEffect(() => {
    if (!show) {
      return;
    }

    setType('product');
    setBrandName('');
    setProductName('');
    setDescription('');
    setQuantity('');
    setConsent(false);
    setMatchConfirmedAt(null);
    setSubmitted(false);
    setSessionLost(false);
    setDebouncedMatch({ term: '', brand: '' });
    mutation.reset();
  }, [show]);

  const shownMatches = matchRequest.data?.matches ?? [];
  // Identity of the list on screen. Every further keystroke produces a new query
  // key, so `data` first drops to undefined and the signature to '' - either way
  // a changed signature means the customer is looking at a different list than
  // the one they confirmed.
  const matchSignature = shownMatches
    .map((match) => `${match.type}:${match.url}`)
    .join('|');

  useEffect(() => {
    setMatchConfirmedAt(null);
  }, [matchSignature]);

  const switchType = (nextType: ListingRequestType) => {
    setType(nextType);
    // Drop the pending debounce value - otherwise one match request fires with
    // the new type and the old term.
    setDebouncedMatch({ term: '', brand: '' });
  };

  const isExactMatch = !!matchRequest.data?.exact;
  // As long as suggestions are on screen the customer has to state that none of
  // them is the one they want. An exact match blocks the request anyway.
  const needsMatchConfirmation = !isExactMatch && shownMatches.length > 0;
  // The description is optional for both request types (P-4972 review).
  const canSubmit =
    consent &&
    !isExactMatch &&
    (!needsMatchConfirmation || null !== matchConfirmedAt) &&
    brandName.trim().length > 0 &&
    (!isProduct || productName.trim().length > 0);

  // Rendered below whichever field drives the check - the product name, or the brand in brand mode.
  const matchHint = matchRequest.data ? (
    <AssortmentMatchHint
      matches={matchRequest.data.matches}
      exact={matchRequest.data.exact}
      confirmed={null !== matchConfirmedAt}
      onConfirmedChange={(confirmed) =>
        setMatchConfirmedAt(confirmed ? localIsoString(new Date()) : null)
      }
      onNavigate={onHide}
    />
  ) : null;

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (mutation.isPending || !canSubmit) {
      return;
    }

    const payload: IListingRequestPayload = {
      type,
      source,
      brand_name: brandName.trim(),
      product_name: isProduct ? productName.trim() : '',
      description: description.trim(),
      quantity_per_month: quantity.trim(),
      consent,
      match_confirmed_at: matchConfirmedAt,
      // Both fields describe the same moment, so the list only travels with a
      // confirmation. The backend re-runs the check and fills in its own hits
      // if the client sent none.
      match_suggestions: matchConfirmedAt ? shownMatches.map(toSuggestion) : [],
    };

    setSessionLost(false);
    mutation.mutate(JSON.stringify(payload), {
      onSuccess: (data) => {
        // A dead session answers with a redirect to the login page, which axios follows to a
        // 200 HTML response - only a JSON body with an id is a real success.
        if (data && typeof data === 'object' && 'id' in data) {
          setSubmitted(true);
          // getRequest wraps a string key in an array, so this matches every reader of the list.
          queryClient.invalidateQueries({
            queryKey: [LISTING_REQUESTS_MINE_QUERY_KEY],
          });
        } else {
          setSessionLost(true);
        }
      },
    });
  };

  return (
    <Modal1
      show={show}
      title={submitted ? 'Thanks for your feedback!' : 'Request Listing'}
      onHide={onHide}
    >
      {submitted ? (
        <>
          <p>We’re reviewing your request and will keep you posted.</p>
          <p className="mb-0">
            You can check the current status anytime under{' '}
            <Link to="/my-listing-requests" onClick={onHide}>
              My Listing Requests
            </Link>
            .
          </p>
        </>
      ) : (
        <form onSubmit={onSubmit} className="contactform">
          <div className="row mb-3">
            <div className="col-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="listingRequestType"
                  id="listingRequestTypeBrand"
                  checked={!isProduct}
                  onChange={() => switchType('brand')}
                />
                <label
                  className="form-check-label"
                  htmlFor="listingRequestTypeBrand"
                >
                  Brand
                </label>
              </div>
            </div>
            <div className="col-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="listingRequestType"
                  id="listingRequestTypeProduct"
                  checked={isProduct}
                  onChange={() => switchType('product')}
                />
                <label
                  className="form-check-label"
                  htmlFor="listingRequestTypeProduct"
                >
                  Product
                </label>
              </div>
            </div>
          </div>

          {isProduct && (
            <div className="mb-3">
              <input
                className="form-control"
                type="text"
                placeholder="Product name*"
                maxLength={255}
                value={productName}
                onChange={(event) => setProductName(event.currentTarget.value)}
              />
              {matchHint}
            </div>
          )}

          <div className="mb-3">
            <input
              className="form-control"
              type="text"
              placeholder="Brand*"
              maxLength={255}
              value={brandName}
              onChange={(event) => setBrandName(event.currentTarget.value)}
            />
            {!isProduct && matchHint}
          </div>

          <div className="mb-3">
            <textarea
              className="form-control"
              rows={4}
              placeholder="Description"
              maxLength={2000}
              value={description}
              onChange={(event) => setDescription(event.currentTarget.value)}
            />
          </div>

          <div className="mb-3">
            <input
              className="form-control"
              type="text"
              placeholder="Quantity/month"
              maxLength={255}
              value={quantity}
              onChange={(event) => setQuantity(event.currentTarget.value)}
            />
          </div>

          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="listingRequestConsent"
              checked={consent}
              onChange={(event) => setConsent(event.currentTarget.checked)}
            />
            <label className="form-check-label" htmlFor="listingRequestConsent">
              I agree to receive email updates about this request.*
            </label>
          </div>

          {mutation.isError && (
            <p className="text-danger">{mutation.error?.message}</p>
          )}
          {sessionLost && (
            <p className="text-danger">
              Your session has expired. Please reload the page and log in again.
            </p>
          )}

          <div className="text-end">
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={!canSubmit}
            >
              Send
            </Button>
          </div>
        </form>
      )}
    </Modal1>
  );
};

/** Drops anything the API might add on top of the four agreed fields. */
function toSuggestion(match: IAssortmentMatch): IAssortmentMatch {
  return {
    type: match.type,
    name: match.name,
    brand_name: match.brand_name,
    url: match.url,
  };
}

/**
 * Date.toISOString() reports UTC, which moves the confirmation into the previous
 * or next day for procurement. This keeps the customer's wall clock and appends
 * their offset instead: 2026-09-18T14:03:11+02:00.
 */
function localIsoString(date: Date): string {
  const offsetMinutes = -date.getTimezoneOffset();
  const absoluteOffset = Math.abs(offsetMinutes);
  const sign = offsetMinutes < 0 ? '-' : '+';

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}` +
    `${sign}${pad(Math.floor(absoluteOffset / 60))}:${pad(absoluteOffset % 60)}`
  );
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export default ListingRequestModal;
